import React from 'react';
import { Box, Button, Typography, Fade, useTheme } from '@mui/material';
import { ChatMessage, shouldShowExpandChip } from './chatUtils';
import ReadAloudButton from '../Voice/ReadAloudButton';

type ExpandableAssistantMessageProps = {
  message: ChatMessage;
  renderContent: (content: string) => React.ReactNode;
  onExpand: (messageId: string) => void;
  isExpanding: boolean;
  showTyping?: boolean;
  typingIndicator?: React.ReactNode;
  readAloudDisabled?: boolean;
};

const ExpandableAssistantMessage: React.FC<ExpandableAssistantMessageProps> = ({
  message,
  renderContent,
  onExpand,
  isExpanding,
  showTyping = false,
  typingIndicator,
  readAloudDisabled = false,
}) => {
  const theme = useTheme();
  const showChip = shouldShowExpandChip(message) && !isExpanding;
  const canReadAloud = Boolean(message.content?.trim()) && !showTyping;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 4 }}>
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              width: 32, height: 32,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, mt: 0.5,
            }}
          >
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
              K
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {showTyping && !message.content ? (
              typingIndicator
            ) : (
              <>
                {canReadAloud && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 0.5, mt: -0.5 }}>
                    <ReadAloudButton
                      id={`assistant-${message.id}`}
                      text={message.content}
                      disabled={readAloudDisabled}
                      ariaLabel="Read tutor response aloud"
                      size="small"
                    />
                  </Box>
                )}
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.primary',
                    lineHeight: 1.6,
                    wordBreak: 'break-word',
                    '& p': { margin: '0 0 12px 0' },
                    '& p:last-child': { margin: 0 },
                    fontSize: '15px',
                  }}
                >
                  {renderContent(message.content)}
                </Typography>
                <Fade in={showChip || message.isExpanded}>
                  <Box sx={{ mt: 1.5 }}>
                    {message.isExpanded ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        Expanded
                      </Typography>
                    ) : showChip ? (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={isExpanding}
                        onClick={() => onExpand(message.id)}
                        sx={{
                          textTransform: 'none',
                          border: '2px solid',
                          borderColor: 'primary.main',
                          borderRadius: 2,
                        }}
                      >
                        {isExpanding ? 'Expanding…' : 'Expand explanation'}
                      </Button>
                    ) : null}
                  </Box>
                </Fade>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ExpandableAssistantMessage;
